import React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md';
  border?: boolean;
  hoverable?: boolean;
  className?: string;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  padding = 'md',
  shadow = 'sm',
  border = true,
  hoverable = false,
  className = '',
  onClick,
  ...rest
}) => {
  // Base styles - rounded-lg is most common in platform
  const baseStyles = 'bg-white rounded-lg transition';

  // Padding styles
  const paddingStyles = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  // Shadow styles
  const shadowStyles = {
    none: '',
    sm: 'shadow-card',
    md: 'shadow-dropdown',
  };

  // Border styles
  const borderStyles = border ? 'border border-neutral-200' : '';

  // Hoverable styles
  const hoverStyles = hoverable ? 'hover:shadow-card-hover cursor-pointer' : '';

  // Interactive styles
  const interactiveStyles = onClick ? 'cursor-pointer' : '';

  const combinedClassName = `${baseStyles} ${paddingStyles[padding]} ${shadowStyles[shadow]} ${borderStyles} ${hoverStyles} ${interactiveStyles} ${className}`.trim();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={combinedClassName}
      onClick={onClick}
      {...(onClick && {
        role: 'button',
        tabIndex: 0,
        onKeyDown: handleKeyDown
      })}
      {...rest}
    >
      {children}
    </div>
  );
};

Card.displayName = 'Card';
